import { useCallback, useEffect, useRef } from 'react';
import { uploadsService } from '@/shared/api/services/uploads.service';
import {
  useConnectionStore,
  ConnectionStatus,
} from '@/shared/api/websocket/stores/connection.store';
import { wsLogger } from '@/shared/lib/logger';
import type { ValidationItem } from './types';
import { settledUploadPatch, type UploadRowCopy } from './decode-validation';

/** Absorbs reconnect / tab-focus flap so neither can storm the upload list. */
const RECONCILE_THROTTLE_MS = 10_000;

interface UseValidationReconcileProps {
  /** Building the intakes belong to — the list endpoint is building-scoped. */
  projectId: string;
  validationItems: ValidationItem[];
  setValidationItems: React.Dispatch<React.SetStateAction<ValidationItem[]>>;
  /** Fallback row text when the server sent none. */
  copy: UploadRowCopy;
}

/**
 * L2 (socket reconnect) and L3 (tab visible) reconcile for uploads in flight.
 *
 * The socket can miss an `uploads/completed` while it is down or the tab is
 * hidden, which leaves a row spinning until its own timeout. One request asks
 * the building which of its uploads are still unfinished; any watched id that
 * is missing from that answer has a result waiting, and is read once through
 * the same polling point. A failed reconcile changes nothing — the per-upload
 * poll keeps running underneath.
 */
export function useValidationReconcile({
  projectId,
  validationItems,
  setValidationItems,
  copy,
}: UseValidationReconcileProps) {
  // Read, never re-subscribed on — see useValidationPolling.
  const copyRef = useRef(copy);
  copyRef.current = copy;
  const itemsRef = useRef(validationItems);
  itemsRef.current = validationItems;

  const lastReconcileAtRef = useRef(0);
  const status = useConnectionStore((state) => state.status);
  const previousStatusRef = useRef<ConnectionStatus>(status);

  const reconcile = useCallback(
    async (reason: string) => {
      if (!projectId) return;

      const watched = itemsRef.current.filter(
        (item) => item.status === 'validating'
      );
      if (watched.length === 0) return;

      const now = Date.now();
      if (now - lastReconcileAtRef.current < RECONCILE_THROTTLE_MS) return;
      lastReconcileAtRef.current = now;

      try {
        const pending = await uploadsService.listPending(projectId);
        const stillRunning = new Set(pending.map((upload) => upload.id));

        for (const item of watched) {
          if (stillRunning.has(item.id)) continue;

          const upload = await uploadsService.get(projectId, item.id);
          const patch = settledUploadPatch(upload, copyRef.current);
          if (!patch) continue;

          wsLogger.info(`[RECONCILE] intake ${item.id} settled (${reason})`);
          setValidationItems((prev) =>
            prev.map((vi) => (vi.id === item.id ? { ...vi, ...patch } : vi))
          );
        }
      } catch (error) {
        wsLogger.error('[RECONCILE] intake reconcile failed:', error);
      }
    },
    [projectId, setValidationItems]
  );

  // L2: the socket came back after being away, so an announcement may have
  // landed in the gap. A cold start has nothing to reconcile against.
  useEffect(() => {
    const cameBack =
      status === ConnectionStatus.CONNECTED &&
      previousStatusRef.current !== ConnectionStatus.CONNECTED &&
      previousStatusRef.current !== ConnectionStatus.DISCONNECTED;

    previousStatusRef.current = status;

    if (cameBack) {
      void reconcile('ws-reconnect');
    }
  }, [status, reconcile]);

  // L3: the tab was hidden, where a browser may freeze the socket without any
  // state change to react to.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void reconcile('visible');
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [reconcile]);
}
